import { defineStore } from 'pinia'
import {
  acceptRestaurantOrderApi,
  fetchRestaurantOrderByIdApi,
  fetchRestaurantOrdersApi,
  markRestaurantOrderReadyApi,
  rejectRestaurantOrderApi,
  RESTAURANT_ORDERS_LIST_TAB,
  RESTAURANT_ORDERS_MOBILE_PAGE_SIZE,
  sortRestaurantOrdersNewestFirst,
} from '@/entities/order/api/restaurantOrdersApi'

export const useRestaurantOrdersStore = defineStore('restaurantOrders', {
  state: () => ({
    selectedOrderId: null,
    activeOrder: null,
    isActiveOrderLoading: false,
    isActiveOrderUpdating: false,
    activeOrderError: null,
    activeOrderRequestId: 0,
    listTab: RESTAURANT_ORDERS_LIST_TAB.ACTIVE,
    listSkip: 0,
    listLimit: RESTAURANT_ORDERS_MOBILE_PAGE_SIZE,
    orderListTotal: 0,
    orderList: [],
    isOrderListLoading: false,
    orderListError: null,
    listRequestId: 0,
  }),
  getters: {
    listPageIndex: (state) =>
      state.listLimit > 0 ? Math.floor(state.listSkip / state.listLimit) : 0,
    listPageCount: (state) =>
      state.listLimit > 0 ? Math.max(1, Math.ceil(state.orderListTotal / state.listLimit)) : 1,
  },
  actions: {
    clear() {
      this.selectedOrderId = null
      this.activeOrder = null
      this.isActiveOrderLoading = false
      this.isActiveOrderUpdating = false
      this.activeOrderError = null
      this.activeOrderRequestId = 0
      this.listTab = RESTAURANT_ORDERS_LIST_TAB.ACTIVE
      this.listSkip = 0
      this.listLimit = RESTAURANT_ORDERS_MOBILE_PAGE_SIZE
      this.orderListTotal = 0
      this.orderList = []
      this.isOrderListLoading = false
      this.orderListError = null
      this.listRequestId = 0
    },

    setSelectedOrderId(orderId) {
      const nextId = orderId ? String(orderId) : null
      this.selectedOrderId = nextId
      if (!nextId || (this.activeOrder && String(this.activeOrder.id) !== nextId)) {
        this.activeOrder = null
      }
    },

    setListTab(tab) {
      const isNewTab = this.listTab !== tab
      this.listTab = tab
      if (!isNewTab) {
        return
      }
      this.listSkip = 0
      return  this.fetchOrderList()
    },

    setListPage(pageIndex) {
      const nextSkip = Math.max(0, Number(pageIndex) || 0) * this.listLimit
      if (nextSkip === this.listSkip) {
        return
      }
      this.listSkip = nextSkip
      return this.fetchOrderList()
    },

    setListLimit(limit) {
      const nextLimit = Math.max(1, Number(limit) || RESTAURANT_ORDERS_MOBILE_PAGE_SIZE)
      if (nextLimit !== this.listLimit) {
        this.listLimit = nextLimit
        this.listSkip = 0
      }
      return this.fetchOrderList()
    },

    mergeOrderInList(order) {
      if (!order?.id) {
        return
      }
      const index = this.orderList.findIndex((item) => item.id === order.id)
      if (index >= 0) {
        this.orderList[index] = order
        return
      }
      this.orderList = sortRestaurantOrdersNewestFirst([...this.orderList, order])
    },

    async syncSelectedOrderFromServer() {
      const orderId = this.selectedOrderId ?? this.activeOrder?.id
      if (!orderId) {
        return
      }
      try {
        const order = await fetchRestaurantOrderByIdApi(orderId)
        if (order) {
          this.activeOrder = order
          this.mergeOrderInList(order)
        }
      } catch {}
    },

    async fetchOrderList() {
      const requestId = ++this.listRequestId
      const tab = this.listTab
      const skip = this.listSkip
      const limit = this.listLimit

      this.isOrderListLoading = true
      this.orderListError = null
      try {
        const { items, total } = await fetchRestaurantOrdersApi({
          skip,
          limit,
          listTab: tab,
        })
        if (requestId !== this.listRequestId) {
          return
        }
        this.orderList = items
        this.orderListTotal = total
      } catch (error) {
        if (requestId !== this.listRequestId) {
          return
        }
        this.orderListError = error
        this.orderList = []
        this.orderListTotal = 0
      } finally {
        if (requestId === this.listRequestId) {
          this.isOrderListLoading = false
        }
      }
    },

    async loadSelectedOrder(orderId) {
      const id = orderId ?? this.selectedOrderId
      if (!id) {
        this.activeOrder = null
        this.isActiveOrderLoading = false
        return
      }

      if (String(id) !== String(this.selectedOrderId)) {
        this.setSelectedOrderId(id)
      }

      const requestId = ++this.activeOrderRequestId
      this.isActiveOrderLoading = true
      this.activeOrderError = null
      if (!this.activeOrder || String(this.activeOrder.id) !== String(id)) {
        this.activeOrder = null
      }

      try {
        const order = await fetchRestaurantOrderByIdApi(id)
        if (requestId !== this.activeOrderRequestId) {
          return
        }
        this.activeOrder = order
        if (order) {
          this.mergeOrderInList(order)
        }
      } catch (error) {
        if (requestId !== this.activeOrderRequestId) {
          return
        }
        this.activeOrderError = error
        this.activeOrder = null
      } finally {
        if (requestId === this.activeOrderRequestId) {
          this.isActiveOrderLoading = false
        }
      }
    },

    async rejectActiveOrder() {
      if (!this.activeOrder || this.isActiveOrderUpdating) {
        return
      }

      this.isActiveOrderUpdating = true
      this.activeOrderError = null
      try {
        const updated = await rejectRestaurantOrderApi(this.activeOrder.id)
        if (updated) {
          this.activeOrder = updated
        }
        await this.fetchOrderList()
      } catch (error) {
        await this.syncSelectedOrderFromServer()
        throw error
      } finally {
        this.isActiveOrderUpdating = false
      }
    },

    async acceptActiveOrder({ pickupReadyAt }) {
      if (!this.activeOrder || this.isActiveOrderUpdating) {
        return
      }

      this.isActiveOrderUpdating = true
      this.activeOrderError = null
      try {
        const updated = await acceptRestaurantOrderApi(this.activeOrder.id, {
          pickupReadyAt,
        })
        if (updated) {
          this.activeOrder = updated
        }
        await this.fetchOrderList()
      } catch (error) {
        await this.syncSelectedOrderFromServer()
        throw error
      } finally {
        this.isActiveOrderUpdating = false
      }
    },

    async markReadyForPickup() {
      if (!this.activeOrder || this.isActiveOrderUpdating) {
        return
      }

      this.isActiveOrderUpdating = true
      this.activeOrderError = null
      try {
        const updated = await markRestaurantOrderReadyApi(this.activeOrder.id)
        if (updated) {
          this.activeOrder = updated
        }
        await this.fetchOrderList()
      } catch (error) {
        await this.syncSelectedOrderFromServer()
        throw error
      } finally {
        this.isActiveOrderUpdating = false
      }
    },
  },
})
